import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Upload,
  X,
  AlertCircle,
  LifeBuoy,
  Bug,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { insertData, uploadFile, isSupabaseConfigured } from '../lib/supabase';
import { trackEvent } from '../lib/analyticsEvents';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 Mo
const MAX_FILES = 5;
const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
];

const categories = [
  { value: 'bug', label: 'Bug / Erreur' },
  { value: 'affichage', label: "Problème d'affichage" },
  { value: 'performance', label: 'Lenteur / Performance' },
  { value: 'contenu', label: 'Contenu à modifier' },
  { value: 'fonctionnalite', label: 'Nouvelle fonctionnalité' },
  { value: 'autre', label: 'Autre' },
];

const priorities = [
  { value: 'basse', label: '🟢 Basse — Pas urgent' },
  { value: 'normale', label: '🟡 Normale — Sous quelques jours' },
  { value: 'haute', label: '🟠 Haute — Bloquant partiel' },
  { value: 'critique', label: '🔴 Critique — Site inutilisable' },
];

export const ReportIssue = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    website: '',
    category: '',
    priority: '',
    subject: '',
    description: '',
    stepsToReproduce: '',
    browser: '',
  });
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    trackEvent('view_report_issue_page', {
      page_path: window.location.pathname,
    });

    // Détection du navigateur
    if (typeof window !== 'undefined') {
      const ua = navigator.userAgent;
      let browserInfo = 'Inconnu';
      if (ua.includes('Firefox')) browserInfo = `Firefox — ${navigator.platform}`;
      else if (ua.includes('Edg')) browserInfo = `Edge — ${navigator.platform}`;
      else if (ua.includes('Chrome')) browserInfo = `Chrome — ${navigator.platform}`;
      else if (ua.includes('Safari')) browserInfo = `Safari — ${navigator.platform}`;
      setFormData((prev) => ({ ...prev, browser: browserInfo }));
    }
  }, []);

  const pageTitle = 'Signaler un problème | Support Weblax';
  const pageDescription =
    "Ouvrez un ticket de support Weblax : décrivez votre problème, joignez des captures d'écran et recevez une réponse rapide.";
  const pageUrl = 'https://weblax.fr/signaler-un-probleme';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateAndAddFiles = (newFiles) => {
    const filesArray = Array.from(newFiles);

    if (files.length + filesArray.length > MAX_FILES) {
      toast.error(`Vous pouvez joindre au maximum ${MAX_FILES} captures.`);
      return;
    }

    const validFiles = [];
    for (const file of filesArray) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`${file.name} : format non supporté (PNG, JPG, GIF, WEBP).`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} dépasse 5 Mo.`);
        continue;
      }
      validFiles.push({
        file,
        preview: URL.createObjectURL(file),
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      });
    }
    setFiles((prev) => [...prev, ...validFiles]);
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.length) validateAndAddFiles(e.target.files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) validateAndAddFiles(e.dataTransfer.files);
  };

  const removeFile = (id) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  useEffect(() => {
    return () => {
      files.forEach((f) => URL.revokeObjectURL(f.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadScreenshots = async () => {
    const urls = [];
    for (const { file } of files) {
      const ext = file.name.split('.').pop();
      const timestamp = Date.now();
      const random = Math.random().toString(36).slice(2, 8);
      const path = `${timestamp}-${random}.${ext}`;

      const { publicUrl, error } = await uploadFile('ticket-screenshots', path, file);
      if (error) throw error;
      if (publicUrl) urls.push({ url: publicUrl, name: file.name });
    }
    return urls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.firstName ||
      !formData.email ||
      !formData.subject ||
      !formData.description
    ) {
      toast.error('Veuillez remplir les champs obligatoires (*)');
      return;
    }

    setIsLoading(true);

    try {
      // 1. Upload des captures
      let screenshots = [];
      if (files.length > 0) {
        toast.info(`Envoi des ${files.length} capture(s)...`);
        screenshots = await uploadScreenshots();
      }

      // 2. Insertion en base
      const { error, demo } = await insertData('tickets', {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        company: formData.company,
        website: formData.website,
        category: formData.category,
        priority: formData.priority || 'normale',
        subject: formData.subject,
        description: formData.description,
        steps_to_reproduce: formData.stepsToReproduce,
        browser: formData.browser,
        screenshot_urls: screenshots.map((s) => s.url),
        status: 'new',
        created_at: new Date().toISOString(),
      });

      if (error) throw error;

      // 3. Envoi de l'email via Edge Function
      if (!demo && isSupabaseConfigured) {
        try {
          const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-issue-notification`;

          const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
              apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              company: formData.company,
              website: formData.website,
              category: formData.category,
              priority: formData.priority || 'normale',
              subject: formData.subject,
              description: formData.description,
              stepsToReproduce: formData.stepsToReproduce,
              browser: formData.browser,
              screenshots,
            }),
          });

          if (!response.ok) {
            const errText = await response.text();
            console.error('Edge Function error:', response.status, errText);
          }
        } catch (err) {
          console.error("Erreur envoi email ticket :", err);
        }
      }

      if (demo) {
        toast.success('Ticket envoyé ! (Mode démo - Supabase non configuré)');
      } else {
        toast.success('Ticket envoyé ! Nous vous répondrons rapidement.');
      }

      trackEvent('report_issue_submit', {
        category: formData.category || 'unknown',
        priority: formData.priority || 'normale',
        has_screenshots: screenshots.length > 0,
        screenshot_count: screenshots.length,
        page_path: window.location.pathname,
      });

      // Reset
      files.forEach((f) => URL.revokeObjectURL(f.preview));
      setFiles([]);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        website: '',
        category: '',
        priority: '',
        subject: '',
        description: '',
        stepsToReproduce: '',
        browser: formData.browser,
      });
    } catch (err) {
      console.error(err);
      toast.error(`Une erreur est survenue : ${err.message || 'Réessayez plus tard.'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Signaler un problème — Weblax',
    url: pageUrl,
    description: pageDescription,
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div data-testid="report-issue-page">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-28 text-center">
              <p className="weblax-caption text-primary mb-4 inline-flex items-center gap-2 justify-center">
                <LifeBuoy className="w-4 h-4" /> Support
              </p>
              <h1 className="weblax-heading-display mb-6 max-w-3xl mx-auto">
                Signaler un <span className="weblax-gradient-text">problème</span>
              </h1>
              <p className="weblax-body-lg max-w-2xl mx-auto">
                Décrivez ce qui ne va pas et joignez des captures d&apos;écran.
                Plus le contexte est précis, plus la résolution est rapide.
              </p>
            </div>
          </div>
        </section>

        {/* Formulaire */}
        <section className="weblax-section">
          <div className="weblax-container">
            <div className="max-w-3xl mx-auto">
              <Card className="weblax-card">
                <CardContent className="p-6 md:p-8">
                  {!isSupabaseConfigured && (
                    <div className="mb-6 flex items-start gap-3 text-sm bg-muted p-3 rounded-lg">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <p>Mode démo — Supabase non configuré. L&apos;envoi sera simulé.</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Coordonnées */}
                    <div>
                      <h3 className="weblax-heading-3 mb-4">Vos coordonnées</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">Prénom *</Label>
                          <Input
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            data-testid="issue-firstname"
                            className="weblax-input"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">Nom</Label>
                          <Input
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            data-testid="issue-lastname"
                            className="weblax-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="votre@email.com"
                            data-testid="issue-email"
                            className="weblax-input"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company">Entreprise</Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            data-testid="issue-company"
                            className="weblax-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contexte */}
                    <div>
                      <h3 className="weblax-heading-3 mb-4">Contexte</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="website">Site / URL concernée</Label>
                          <Input
                            id="website"
                            name="website"
                            value={formData.website}
                            onChange={handleChange}
                            placeholder="https://..."
                            data-testid="issue-website"
                            className="weblax-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="category">Catégorie</Label>
                          <Select
                            value={formData.category}
                            onValueChange={(v) => handleSelectChange('category', v)}
                          >
                            <SelectTrigger data-testid="issue-category" className="weblax-input">
                              <SelectValue placeholder="Choisir une catégorie" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((c) => (
                                <SelectItem key={c.value} value={c.value}>
                                  {c.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="priority">Priorité</Label>
                          <Select
                            value={formData.priority}
                            onValueChange={(v) => handleSelectChange('priority', v)}
                          >
                            <SelectTrigger data-testid="issue-priority" className="weblax-input">
                              <SelectValue placeholder="Choisir un niveau de priorité" />
                            </SelectTrigger>
                            <SelectContent>
                              {priorities.map((p) => (
                                <SelectItem key={p.value} value={p.value}>
                                  {p.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <h3 className="weblax-heading-3 mb-4">Description du problème</h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="subject">Objet du ticket *</Label>
                          <Input
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="Résumé en une phrase"
                            data-testid="issue-subject"
                            className="weblax-input"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="description">Description détaillée *</Label>
                          <Textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Que se passe-t-il ? Quel est le comportement attendu ?"
                            data-testid="issue-description"
                            className="weblax-input min-h-[130px]"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="stepsToReproduce">
                            Étapes pour reproduire (facultatif)
                          </Label>
                          <Textarea
                            id="stepsToReproduce"
                            name="stepsToReproduce"
                            value={formData.stepsToReproduce}
                            onChange={handleChange}
                            placeholder={
                              '1. Aller sur la page...\n2. Cliquer sur...\n3. Constater que...'
                            }
                            data-testid="issue-steps"
                            className="weblax-input min-h-[100px]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Captures */}
                    <div>
                      <h3 className="weblax-heading-3 mb-2">Captures d&apos;écran</h3>
                      <p className="weblax-body text-sm mb-4">
                        Jusqu&apos;à {MAX_FILES} fichiers, 5 Mo max chacun (PNG, JPG, GIF, WEBP)
                      </p>

                      <div
                        onDrop={handleDrop}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDragging(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setIsDragging(false);
                        }}
                        onClick={() => fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
                          isDragging
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50 hover:bg-primary/5'
                        }`}
                        data-testid="issue-dropzone"
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept={ACCEPTED_TYPES.join(',')}
                          onChange={handleFileSelect}
                          className="hidden"
                          data-testid="issue-file-input"
                        />
                        <Upload className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                        <p className="font-medium mb-1">
                          Glissez vos captures ici ou cliquez pour parcourir
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {files.length}/{MAX_FILES} fichier(s) sélectionné(s)
                        </p>
                      </div>

                      {files.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                          {files.map(({ file, preview, id }) => (
                            <div
                              key={id}
                              className="relative group border border-border rounded-xl overflow-hidden bg-muted"
                            >
                              <img
                                src={preview}
                                alt={file.name}
                                className="w-full h-32 object-cover"
                              />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFile(id);
                                  }}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Retirer
                                </Button>
                              </div>
                              <div className="p-2 text-xs">
                                <p className="truncate font-medium">{file.name}</p>
                                <p className="text-muted-foreground">
                                  {formatBytes(file.size)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {formData.browser && (
                      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
                        <Bug className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <p>
                          Navigateur détecté : <strong>{formData.browser}</strong>{' '}
                          (envoyé automatiquement)
                        </p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isLoading}
                      data-testid="issue-submit"
                      className="weblax-btn-primary w-full md:w-auto"
                    >
                      {isLoading ? 'Envoi en cours...' : 'Envoyer le ticket'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <p className="text-center text-sm text-muted-foreground mt-6">
                Besoin d&apos;autre chose ?{' '}
                <Link to="/contact" className="text-primary hover:underline">
                  Aller au formulaire de contact classique
                </Link>
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ReportIssue;